import { Component } from 'react';
import { Card, Button } from 'react-bootstrap';
import { GoX } from 'react-icons/go';
import Tooltip from "../../../controls/common/tooltip";
import { Converter } from "../../workspace/converter";
import SessionContext from "../../session/context";
import AliasWatch from "../../session/aliasWatch";
import WidgetStatus from "./widgetStatus";
import "./index.css";

/**
 * Removes a widget from the playground definition: drops the node, drops any connections that
 * referenced it (free-form only — grid playgrounds carry no connections, so the filter is a no-op),
 * and clears the details pane.
 *
 * Exported because there are now three ways to remove a widget — the header "×" and the Delete key on
 * each of the two workspace layouts — and they must all take exactly the same path.
 */
export function removeComponentById(shellInterface, componentId) {
  if ( !shellInterface || !shellInterface.getState || !componentId ) return;
  const state = shellInterface.getState();
  const workflowInterface = state.workflowInterface;
  const nextNodes = (state.nodes || []).filter((n) => n.id !== componentId);
  if ( workflowInterface && workflowInterface.setNodes ) {
    workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  }
  const nextEdges = (state.edges || []).filter(
    (e) => e.source !== componentId && e.target !== componentId
  );
  if ( workflowInterface && workflowInterface.setEdges && nextEdges.length !== (state.edges || []).length ) {
    workflowInterface.setEdges(nextEdges, /*isModified=*/true);
  }
  if ( shellInterface.updateDetails ) {
    shellInterface.updateDetails(null, null);
  }
}

export default class WidgetBase extends Component {
  static contextType = SessionContext;

  // Last-resort free-form dimensions, used only when neither the component nor its manifest
  // carries a sizing port. Matches the most common manifest defaults.
  static FALLBACK_WIDTH = 700;
  static FALLBACK_HEIGHT = 500;

  // What this widget reads from the session. Lazy, so a bare `Object.create(WidgetBase.prototype)` — how the
  // suites build a widget — has one too.
  get objects() {
    if (!this._objects) {
      this._objects = new AliasWatch(() => this.context && this.context.objects, () => this.forceUpdate());
    }
    return this._objects;
  }

  loadObjectValue(component, route, type) {
    const alias = Converter.getComponentPortValueData(component, route);
    if ( !alias ) {
      return [null, { variant: "info", message: `Connect a value to the "${route}" port.` }];
    }

    const manager = this.context;
    if ( !manager || !manager.session ) {
      return [null, { variant: "info", message: "Select a session to view this widget." }];
    }

    // Reading is what asks for it: see session/aliasWatch.js.
    const entry = this.objects.read(alias);

    if ( "missing" === entry.status ) {
      return [null, { variant: "info", message: `"${alias}" is not available in the selected session yet.` }];
    }
    if ( "error" === entry.status ) {
      return [null, { variant: "warning", message: `Could not load "${alias}".` }];
    }
    if ( "ready" !== entry.status ) {
      return [null, { variant: "primary", loading: true, message: `Loading "${alias}"…` }];
    }

    const value = entry.value;
    const valueType = value && value["type"];
    if ( type !== valueType ) {
      return [null, {
        variant: "warning",
        message: `The "${route}" port expects a "${type}" value, but "${alias}" is "${valueType}".`
      }];
    }

    return [value, null];
  }

  // What a bound object IS, right now — for a widget that renders an envelope (a thread, a graph) and loads its
  // contents through an API of its own, rather than rendering the value itself. Returns:
  //   { status: "unbound" }        — nothing bound to the port
  //   { status: "no-session" }     — an alias, and no session selected
  //   { status: "loading", reason } — asked for, not here yet
  //   { status: "missing", alias }  — the platform says it is not there: a 403, or a delete event
  //   { status: "error", alias }    — the load failed
  //   { status: "wrong-type", alias, type } — it exists and is something else
  //   { status: "ready", value }
  // The session's object LIST is never consulted: it lags every mutation, so it can prove an object exists and
  // can never prove one does not. Reading the entry is also what asks for it (session/aliasWatch.js), so a
  // "missing" object fills in by itself when the event that creates it arrives.
  objectEnvelopeState(alias, type, loadingReason) {
    if ( !alias ) return { status: "unbound" };

    const manager = this.context;
    if ( !manager || !manager.session ) return { status: "no-session" };

    const entry = this.objects.read(alias);
    if ( "missing" === entry.status ) return { status: "missing", alias };
    if ( "error" === entry.status ) return { status: "error", alias };
    if ( "ready" !== entry.status ) return { status: "loading", reason: loadingReason };

    const value = entry.value;
    if ( !value || value.type !== type ) {
      return { status: "wrong-type", alias, type: (value && value.type) || "unknown" };
    }
    return { status: "ready", value };
  }

  /**
   * Is this widget waiting on something? Return a short phrase saying what; null when it is not.
   *
   * Session sync winds itself down after three idle minutes, and only the widget knows whether the
   * screen it draws is idle. A phrase returned here keeps sync running — and starts it, if the
   * person has not switched it off — for as long as the phrase keeps coming back. It is shown on
   * the switch, so answer it with what a person would want to read there: "Chat — execution
   * running", "Missions — 2 cells working".
   *
   * Re-asked after every render, which is what makes it a live answer rather than a subscription to
   * maintain: a widget already re-renders when the thing it is waiting on changes.
   */
  syncHold() {
    return null;
  }

  /** The key this widget's hold is filed under — its node id, so one widget holds one hold. */
  syncHoldKey() {
    const component = this.props && this.props.data && this.props.data.component;
    return (component && component.id) || null;
  }

  applySyncHold() {
    if (this.context && this.context.holdSync) {
      this.context.holdSync(this.syncHoldKey(), this.syncHold());
    }
  }

  componentDidMount() {
    this.objects.commit();
    this.applySyncHold();
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentDidUpdate() {
    this.objects.commit();
    this.applySyncHold();
  }

  componentWillUnmount() {
    this.objects.release();

    // Let go of sync. A widget that is gone is watching nothing, and a hold left behind would keep
    // the session polling for the rest of the page's life.
    if (this.context && this.context.holdSync) {
      this.context.holdSync(this.syncHoldKey(), null);
    }

    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
  }

  renderNotice(notice) {
    if ( !notice ) return null;
    // Pass the variant straight through. General widget alerts use info/warning
    // (standard Alert styling, consistent with the rest of the app);
    // content-internal callers pass "light" for the subtle box.
    return (
      <WidgetStatus variant={notice.variant || "info"} loading={!!notice.loading}>{notice.message}</WidgetStatus>
    );
  }

  renderLoading(message) {
    // Loading is a general widget state -> primary Alert (matches the app-wide
    // "Loading …" convention, e.g. "Loading revision"), with an inline spinner.
    return <WidgetStatus variant="primary" loading>{message}</WidgetStatus>;
  }

  renderBody() {
    return (
      <Card.Body>
        Widget content implementation is missing.
      </Card.Body>
    );
  }

  getClassName() {
    return "";
  }

  /**
   * Resolve a sizing port (width / height) to a positive number of pixels.
   *
   * Ports are materialized onto a component when it is dropped onto the canvas, so a playground
   * saved BEFORE its widget's manifest declared width/height has no port to read. Fall back to
   * the manifest default in that case, and to a sane constant if even that is missing — anything
   * else yields "nullpx", which the browser drops, leaving the widget unsized.
   */
  getSizePortValue(route, fallback) {
    const value = Converter.getComponentPortValueData(this.props.data.component, route);
    if (typeof value === "number" && value > 0) {
      return value;
    }
    const portManifest = (this.props.data?.manifest?.ports || []).find((p) => p["route"] === route);
    const defaultValue = portManifest?.["value"]?.["default"];
    return (typeof defaultValue === "number" && defaultValue > 0) ? defaultValue : fallback;
  }

  // The free-form `width` port sizes the CARD, not the body — see getEffectiveSize. Null in grid,
  // where the cell owns the width.
  getConfiguredWidth() {
    if (this.props.data?.layoutType === "grid") {
      return null;
    }
    return `${this.getSizePortValue("width", WidgetBase.FALLBACK_WIDTH)}px`;
  }

  // Content ALWAYS fills its card.
  //
  // The width port used to be applied to the body while the card was left to shrink-wrap. But a card
  // can't be narrower than its header's minimum, and the header carries a name, an alias chip, action
  // buttons, a type label and a close button — so on any widget configured narrower than that floor,
  // the card came out wider than the body and the content sat in a column with dead space beside it.
  // Putting the width on the card instead means the body tracks it whatever it resolves to.
  //
  // Height stays on the body: nothing competes with it there, and the card has no height floor of its
  // own to fight.
  getEffectiveSize() {
    if (this.props.data?.layoutType === "grid") {
      return { width: "100%", height: "100%", maxHeight: "100%" };
    }
    const h = this.getSizePortValue("height", WidgetBase.FALLBACK_HEIGHT);
    return { width: "100%", height: `${h}px`, maxHeight: `${h}px` };
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    return (
      <Card.Body className="nodrag nowheel" style={{
        width,
        height,
        maxHeight,
        overflow: "scroll"
      }}>
        {this.renderBody()}
      </Card.Body>
    );
  }

  isSelected() {
    const shellInterface = this.props.data?.shellInterface;
    if (!shellInterface || !shellInterface.getState) return false;
    const state = shellInterface.getState();
    const componentId = this.props.data?.component?.id;
    return state?.details?.view === "component" && state?.details?.data?.id === componentId;
  }

  removeComponent = (event) => {
    event.stopPropagation();
    removeComponentById(this.props.data?.shellInterface, this.props.data?.component?.id);
  };

  renderHeaderControls() {
    const canEdit = !!this.props.data?.shellInterface?.canEditRevision?.();
    return (
      <span className="playground-widget-controls float-right" onClick={(e) => e.stopPropagation()}>
        <span className="text-muted playground-widget-type">
          {this.props.data.component.type}
        </span>
        {canEdit && (
          <Tooltip text="Remove widget">
            <Button
              variant="light"
              size="sm"
              onClick={this.removeComponent}
            >
              <GoX />
            </Button>
          </Tooltip>
        )}
      </span>
    );
  }

  // Override points for envelope-backed widgets (Thread, Message). Default
  // to null so generic widgets keep their current header unchanged.
  renderHeaderAlias() {
    return null;
  }

  renderHeaderActions() {
    return null;
  }

  render() {
    // A new pass: what it reads is what stays watched after it.
    this.objects.begin();

    const isGrid = this.props.data?.layoutType === "grid";
    const isSelected = this.isSelected();
    const cardClassName = [
      "playground-widget",
      this.getClassName() || "",
      isGrid ? "playground-widget-grid" : "",
      isSelected ? "playground-widget-selected" : "",
    ].filter(Boolean).join(" ");

    // The header is a permanent in-flow top bar on every widget — always
    // rendered (not just on selection); see common/index.css.
    const renderHeader = true;

    // Per-widget content zoom (grid mode only). Comes from the playground
    // shell's zoom slider — the layout itself stays at 1x so widget cells
    // remain aligned to the grid; only what's inside each cell scales.
    const widgetZoom = this.props.data?.zoom ?? 1;
    const contentStyle = isGrid ? { zoom: widgetZoom } : undefined;

    const configuredWidth = this.getConfiguredWidth();

    return (
      <Card className={cardClassName} style={configuredWidth ? { width: configuredWidth } : undefined}>
        {renderHeader && (
          <Card.Header>
            <span className="playground-widget-header-left">
              <span className="playground-widget-name">
                {this.props.data.component.name}
              </span>
              {this.renderHeaderAlias()}
            </span>
            <span className="playground-widget-header-right">
              {this.renderHeaderActions()}
              {this.renderHeaderControls()}
            </span>
          </Card.Header>
        )}
        <div className="playground-widget-content" style={contentStyle}>
          {this.renderContent()}
        </div>
      </Card>
    );
  }
}
